import { useEffect, useState } from 'react';
import { Outlet, useMatches, useNavigate } from 'react-router-dom';

import { DialogHost } from '@/components/feedback/dialog';
import { useToast } from '@/components/feedback/toast';
import { routes } from '@/config/routes';
import { AUTH_ERROR_CODES, AUTH_ERROR_COPY } from '@/features/auth';
import { onForceLogout } from '@/lib/api/force-logout';
import { useAuth } from '@/providers/auth-provider';
import type { RouteHandle } from '@/types';

import { PageContainer } from '../page-container';
import { TopNav } from '../top-nav';

import styles from './app-shell.module.scss';
import { HeaderSlotContext } from './header-slot';

export function AppShell() {
  const { clearSession } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // D-01: a map surface's route `handle` (see `RouteHandle`) opts out of the standard chrome —
  // <TopNav> floats over an edge-to-edge canvas instead of sitting above a <PageContainer>. `some`,
  // not the leaf match alone, because `useMatches()` returns the whole matched branch and only the
  // page route itself carries the handle.
  const matches = useMatches();
  const fullBleed = matches.some((match) => (match.handle as RouteHandle | undefined)?.fullBleed);

  // The header slot element, held here rather than in <TopNav> because pages
  // render below it in the tree. Callback ref — no effect, no double render.
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);

  // A session can end under the grower in two ways, both signalled by client.ts's response
  // interceptor after it has already cleared the token — no separate logout call is needed, the
  // session is dead server-side either way.
  //
  // `deactivated` (US-112329) is an account state they have to act on, so it keeps its red toast.
  // `expired` is ordinary — a refresh that could not be rescued — and is warning-toned, matching
  // `mutationErrorTone`'s rule that a 401 is a warning and only a 400 is red. Its wording prefers
  // whatever the server said (ADR-0008), falling back to the house copy when the refresh never
  // reached the network and there is nothing to quote.
  useEffect(
    () =>
      onForceLogout((reason) => {
        clearSession();
        navigate(routes.login);

        if (reason.kind === 'deactivated') {
          toast.error({ title: AUTH_ERROR_COPY[AUTH_ERROR_CODES.ACCOUNT_DEACTIVATED] });
          return;
        }

        toast.warning({
          title: reason.message || AUTH_ERROR_COPY[AUTH_ERROR_CODES.SESSION_EXPIRED],
        });
      }),
    [clearSession, navigate, toast],
  );

  return (
    <DialogHost>
      <div className={styles.shell}>
        <TopNav slotRef={setHeaderSlot} floating={fullBleed} />
        <main className={fullBleed ? styles.fullBleedContent : styles.content}>
          <HeaderSlotContext.Provider value={headerSlot}>
            {fullBleed ? (
              <Outlet />
            ) : (
              <PageContainer>
                <Outlet />
              </PageContainer>
            )}
          </HeaderSlotContext.Provider>
        </main>
      </div>
      {/* Web push (ADR-0020). Renders nothing — it configures FCM from the env, listens, and
          lets a foreground message toast. No-ops when the Firebase env vars are unset. */}
    </DialogHost>
  );
}
